import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import MessagingLabel from 'LuxKit/Label/MessagingLabel'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import CartPanelItemTileLayout from './CartPanelItemTileLayout'
import type { CartPanelAccommodationItem } from '../hooks/useCartPanelItems'

interface Props {
  item: CartPanelAccommodationItem
  onToggleInclude: (item: CartPanelAccommodationItem) => void
  onAdjustDays: (item: CartPanelAccommodationItem) => void
  onChangeRoom: (item: CartPanelAccommodationItem) => void
  onDelete: (item: CartPanelAccommodationItem) => void
  isStreaming: boolean
}

function CartPanelDurationUnavailableTile({
  item,
  onToggleInclude,
  onAdjustDays,
  onChangeRoom,
  onDelete,
  isStreaming,
}: Props) {
  const intl = useIntl()

  const handleAdjustDays = useCallback(() => {
    onAdjustDays(item)
  }, [onAdjustDays, item])

  const handleChangeRoom = useCallback(() => {
    onChangeRoom(item)
  }, [onChangeRoom, item])

  const handleDelete = useCallback(() => {
    onDelete(item)
  }, [onDelete, item])

  const durationLabel = item.duration
    ? intl.formatMessage(
        {
          id: 'vzY1Nv2NAQwUIY-kfaRz',
          defaultMessage: '{nights, plural, one {# night} other {# nights}} not available',
          description: 'Attention indicator showing the current stay duration has no matching hotel rate',
        },
        { nights: item.duration },
      )
    : intl.formatMessage({
        id: 'Xmzbp8R31R0qY7HhjltT',
        defaultMessage: 'Duration not available',
        description: 'Attention indicator when no hotel rate matches the itinerary duration',
      })

  const availableLabel = item.availableDurations?.length
    ? intl.formatMessage(
        {
          id: '-H2Q6-Pr1gjIUdXm3JaQ',
          defaultMessage: 'Available: {durations}',
          description: 'Shows which night counts are available for a hotel when the current duration has no rate',
        },
        { durations: item.availableDurations.map(d => intl.formatMessage({ id: 'AsP3VSrHhSASl5Qz8Mji', defaultMessage: '{count, plural, one {# night} other {# nights}}', description: 'Night count label for showing available hotel stay durations in the cart panel' }, { count: d })).join(', ') },
      )
    : undefined

  const content = (
    <Group direction="vertical" gap={4}>
      <Heading variant="heading5">{item.name}</Heading>
      {item.dates && <BodyText variant="medium" colour="neutral-three">{item.dates}</BodyText>}
      {item.roomName && <BodyText variant="medium">{item.roomName}</BodyText>}
      {item.occupancy && <BodyText variant="medium" colour="neutral-three">{item.occupancy}</BodyText>}
    </Group>
  )

  const attention = (
    <Group direction="vertical" gap={4}>
      <MessagingLabel variant="warningLight">
        {durationLabel}
      </MessagingLabel>
      {availableLabel && <BodyText variant="small" colour="neutral-three">{availableLabel}</BodyText>}
    </Group>
  )

  const actions = (
    <Group direction="horizontal" gap={8}>
      <TextButton kind="tertiary" size="small" onClick={handleAdjustDays} disabled={isStreaming}>
        {intl.formatMessage({
          id: 'ZayCP4wyMYOsR09FUgqL',
          defaultMessage: 'Adjust days',
          description: 'Action button to adjust hotel stay duration from the cart panel',
        })}
      </TextButton>
      <TextButton kind="tertiary" size="small" onClick={handleChangeRoom} disabled={isStreaming}>
        {intl.formatMessage({
          id: 'BrfTYdJonj0A3hETLQR8',
          defaultMessage: 'Change room',
          description: 'Link to change the selected room for a hotel in the cart panel',
        })}
      </TextButton>
      <TextButton kind="tertiary" size="small" startIcon={<LineTrashAltIcon />} onClick={handleDelete} disabled={isStreaming}>
        {intl.formatMessage({
          id: 'jDEZjhV3eo-cnxIBHOtR',
          defaultMessage: 'Delete',
          description: 'Button to permanently delete an item from the trip planner cart panel',
        })}
      </TextButton>
    </Group>
  )

  const price = item.pricing.price ? (
    <PriceRowPrice
      price={item.pricing.price}
      priceFormat="preciseDollar"
      saleUnit=""
      size="S"
    />
  ) : undefined

  return (
    <CartPanelItemTileLayout
      item={item}
      onToggleInclude={onToggleInclude}
      content={content}
      attention={attention}
      actions={actions}
      price={price}
    />
  )
}

export default CartPanelDurationUnavailableTile
